/**
 * API Service for Provident Fund Interest
 * Connected to backend endpoints at /api/v1/pf-interest
 */

import { buildApiUrl } from '../utils/apiSetup';
const API_BASE = buildApiUrl('/api/v1/pf-interest');

const authHeaders = (json = true) => {
  const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
};

/**
 * Fetch all PF Interest entries for a financial year.
 * Returns { entries, totals, financial_year }
 */
export async function fetchPFInterestEntries(financialYear, signal) {
  const response = await fetch(`${API_BASE}?fy=${encodeURIComponent(financialYear)}`, {
    headers: authHeaders(false),
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to load PF interest entries');
  }
  return response.json();
}

/**
 * Create a single PF Interest entry.
 */
export async function createPFInterestEntry(payload, signal) {
  const response = await fetch(API_BASE, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to create entry');
  }
  return response.json();
}

/**
 * Update a PF Interest entry.
 */
export async function updatePFInterestEntry(id, payload, signal) {
  const response = await fetch(`${API_BASE}/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to update entry');
  }
  return response.json();
}

/**
 * Delete a PF Interest entry.
 */
export async function deletePFInterestEntry(id, signal) {
  const response = await fetch(`${API_BASE}/${id}`, {
    method: 'DELETE',
    headers: authHeaders(false),
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to delete entry');
  }
  return response.json();
}

/**
 * Bulk upload an Excel file for PF Interest entries.
 */
export async function bulkUploadPFInterest(file, financialYear, signal) {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE}/bulk-upload?fy=${encodeURIComponent(financialYear)}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
    body: formData,
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Bulk upload failed');
  }
  return response.json();
}

/**
 * Fetch calculation for a financial year if it exists
 */
export async function fetchPFInterestCalculation(financialYear, signal) {
  const response = await fetch(`${API_BASE}/calculation?fy=${encodeURIComponent(financialYear)}`, {
    headers: authHeaders(false),
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to fetch calculation');
  }
  return response.json();
}

/**
 * Perform dry-run calculation
 */
export async function calculatePFInterest(payload, signal) {
  const response = await fetch(`${API_BASE}/calculate`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Calculation failed');
  }
  return response.json();
}

/**
 * Finalize annual PF interest calculation
 */
export async function finalizePFInterest(payload, signal) {
  const response = await fetch(`${API_BASE}/finalize`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Finalization failed');
  }
  return response.json();
}

/**
 * Fetch system defaults for a financial year
 */
export async function fetchPFInterestDefaults(financialYear, signal) {
  const response = await fetch(`${API_BASE}/defaults?fy=${encodeURIComponent(financialYear)}`, {
    headers: authHeaders(false),
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to fetch default values');
  }
  return response.json();
}
